/* Monthly report · the period's runs, kilometres and fuel, from live records.
   Design: ConsoleReport.dc.html.

   Honest limits: the model holds runs for the last 30 days only, so the period
   picker offers "Last 30 days" and "This month" (month to date) — never an
   older month. The summary is written by Zana on request; this page never
   invents prose. Downloads (2026-10-28) are the server's report — PDF or
   CSV, for the period on screen or any earlier month — which the classic
   dashboard never had (it saved a CSV only). */
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFleet } from '../data/FleetData';
import { usePage, useShell } from '../Shell';
import { generateReport, saveReportFile } from '../../services/zanaReports';
import { Empty, PageBody, Shim, ShimStats, ShimTable, StaleBanner, n0, plateBeside, plural, useGo, useToast } from '../kit';

const DAY = 86400000;
type Period = '30d' | 'mtd';

const Skeleton: React.FC = () => (
  <div className="wrap" style={{ maxWidth: 880 }}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}><Shim w={280} h={30} /><Shim w={420} h={16} /></div>
    <div className="panel" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Shim w="100%" h={14} /><Shim w="94%" h={14} /><Shim w="70%" h={14} />
    </div>
    <ShimStats />
    <ShimTable />
  </div>
);

interface BusRow {
  id: string; name: string; plate: string;
  runs: number; km: number | null; litres: number | null; ksh: number | null; kmpl: number | null; carried: number | null;
}

const Report: React.FC = () => {
  const f = useFleet();
  const { ask, openAddBus } = useShell();
  const go = useGo();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('30d');
  const { toast } = useToast();
  const [downloading, setDownloading] = useState<string | null>(null);

  const now = new Date();
  const monthName = now.toLocaleDateString('en-GB', { month: 'long' });
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  // Rounded to the minute so the memos below do not recompute on every render.
  const from = period === '30d' ? Math.floor((now.getTime() - 30 * DAY) / 60000) * 60000 : monthStart;
  const periodLabel = period === '30d'
    ? `the last 30 days (${new Date(from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })})`
    : `${monthName} ${now.getFullYear()} so far`;

  /** The server's report for a range ("YYYY-MM-DD"), saved as a file. */
  const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const download = (key: string, range: { from: string; to: string }, format: 'pdf' | 'csv') => {
    if (downloading || !f.org.code) return;
    setDownloading(key);
    generateReport(f.org.code, { ...range, format })
      .then((file) => { saveReportFile(file); toast({ kind: 'success', text: `${file.periodLabel} downloaded` }); })
      .catch((e: Error) => toast({ kind: 'error', text: e.message || 'The report could not be made. Try again in a moment.' }))
      .finally(() => setDownloading(null));
  };
  const onScreen = { from: ymd(new Date(from)), to: ymd(now) };
  const earlier = Array.from({ length: 12 }, (_, i) => {
    const first = new Date(now.getFullYear(), now.getMonth() - 1 - i, 1);
    const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    return { key: ymd(first), label: first.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }), from: ymd(first), to: ymd(last) };
  });
  const runs = useMemo(() => f.runs.filter((r) => r.status !== 'cancelled' && r.type !== 'refuel' && r.date.getTime() >= from), [f.runs, from]);
  const refills = useMemo(() => f.refills.filter((r) => r.at && r.at.getTime() >= from), [f.refills, from]);

  const km = runs.reduce((s, r) => s + (r.km && r.km > 0 ? r.km : 0), 0);
  const kmKnown = runs.some((r) => r.km != null && r.km > 0);
  const carried = runs.reduce((s, r) => s + r.served, 0);
  const fuelKsh = refills.reduce((s, r) => s + (r.amount || 0), 0);
  const litres = refills.reduce((s, r) => s + (r.litres || 0), 0);

  /* Fuel compared with the same length of time just before (refills go back 120 days). */
  const prevFrom = period === '30d' ? from - 30 * DAY : new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const prevTo = period === '30d' ? from : prevFrom + (now.getTime() - monthStart);
  const fuelPrev = f.refills.filter((r) => r.at && r.at.getTime() >= prevFrom && r.at.getTime() < prevTo).reduce((s, r) => s + (r.amount || 0), 0);
  const fuelDelta = fuelPrev > 0 && fuelKsh > 0 ? Math.round(((fuelKsh - fuelPrev) / fuelPrev) * 100) : null;

  const rows = useMemo<BusRow[]>(() => f.buses.map((b) => {
    const br = runs.filter((r) => r.busId === b.id);
    const bf = refills.filter((r) => r.busId === b.id);
    const bkm = br.reduce((s, r) => s + (r.km && r.km > 0 ? r.km : 0), 0);
    const bl = bf.reduce((s, r) => s + (r.litres || 0), 0);
    const bk = bf.reduce((s, r) => s + (r.amount || 0), 0);
    return {
      id: b.id, name: b.name, plate: b.plate, runs: br.length,
      km: bkm > 0 ? bkm : null, litres: bl > 0 ? bl : null, ksh: bk > 0 ? bk : null,
      kmpl: bkm > 0 && bl > 0 ? bkm / bl : null,
      carried: br.length ? br.reduce((s, r) => s + r.served, 0) : null,
    };
  }).filter((r) => r.runs > 0 || r.ksh != null), [f.buses, runs, refills]);

  /* "Only columns with data are shown." */
  const cols = {
    runs: rows.some((r) => r.runs > 0),
    km: rows.some((r) => r.km != null),
    litres: rows.some((r) => r.litres != null),
    ksh: rows.some((r) => r.ksh != null),
    kmpl: rows.some((r) => r.kmpl != null),
    carried: rows.some((r) => r.carried != null && r.carried > 0),
  };

  const thirstiest = rows.filter((r) => r.kmpl != null).sort((a, b) => (a.kmpl as number) - (b.kmpl as number))[0];
  const title = period === '30d' ? 'Last 30 days' : `${monthName} report`;
  const summaryQ = `Write the monthly summary for ${f.org.name || 'our school'} for ${periodLabel}: ${plural(runs.length, 'run')}, ${n0(km)} km, Ksh ${n0(fuelKsh)} of fuel.`;

  usePage({
    key: 'ConsoleReport', title: 'Monthly report',
    zanaContext: `Report · ${period === '30d' ? 'last 30 days' : `${monthName} ${now.getFullYear()}`}`,
    zanaIntro: 'I write the monthly summary. Ask me to change it or explain any number.',
    prompts: [
      'Write this up for the board',
      fuelDelta != null ? `Why was fuel ${Math.abs(fuelDelta)}% ${fuelDelta >= 0 ? 'higher' : 'lower'} than the period before?` : 'Where did the fuel money go?',
      thirstiest ? `Why does ${thirstiest.plate} get only ${thirstiest.kmpl?.toFixed(1)} km per litre?` : 'Which bus drove the most?',
    ],
  });

  const hasData = runs.length > 0 || refills.length > 0;
  const mono = [f.org.name, f.org.termLabel].filter(Boolean).join(' · ');

  return (
    <PageBody loading={f.loading} skeleton={<Skeleton />}>
      <div className={'wrap' + (f.error ? ' stale' : '')} style={{ maxWidth: 920 }}>
        {f.error && <StaleBanner what="records" since={f.updatedAt} onRetry={f.retry} />}
        <div className="phead">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {mono && <span className="mono">{mono}</span>}
            <h1 className="h1">{title}</h1>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <div className="seg" role="group" aria-label="Report period">
              <button type="button" className={period === '30d' ? 'on' : ''} aria-pressed={period === '30d'} onClick={() => setPeriod('30d')}>Last 30 days</button>
              <button type="button" className={period === 'mtd' ? 'on' : ''} aria-pressed={period === 'mtd'} onClick={() => setPeriod('mtd')}>{monthName} so far</button>
            </div>
            <button type="button" className="btn b-line" disabled={!!downloading} onClick={() => download('csv', onScreen, 'csv')}>
              {downloading === 'csv' ? 'Making the CSV…' : 'CSV'}
            </button>
            <button type="button" className="btn b-ink" disabled={!!downloading} onClick={() => download('pdf', onScreen, 'pdf')}
              title={`A PDF of ${period === '30d' ? 'the last 30 days' : `${monthName} so far`}`}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none' }}>
                <path d="M12 3v12M7 10l5 5 5-5" /><path d="M5 21h14" />
              </svg>
              {downloading === 'pdf' ? 'Making the PDF…' : 'Download PDF'}
            </button>
          </div>
        </div>

        {!hasData ? (
          <section className="panel">
            <Empty title={`No runs or refills in ${period === '30d' ? 'the last 30 days' : `${monthName} yet`}`}
              body={f.buses.length === 0
                ? 'Add a bus and its driver. Once drivers tap Start and log refills, this report fills in by itself.'
                : 'The report fills in as drivers finish runs and log refills in the Trasse app.'}>
              {period === 'mtd' && <button type="button" className="btn s b-line" onClick={() => setPeriod('30d')}>Show the last 30 days</button>}
              {f.buses.length === 0 && <button type="button" className="btn s b-ink" onClick={openAddBus}>Add a bus</button>}
            </Empty>
          </section>
        ) : (
          <>
            <section className="panel" style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span className="chip c-z" style={{ alignSelf: 'flex-start' }}>
                <span aria-hidden="true" style={{ width: 14, height: 14, borderRadius: 4, background: 'var(--z)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2, flex: 'none' }}>
                  <i style={{ width: 3, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} />
                  <i style={{ width: 3, height: 4, borderRadius: 3, background: '#fff', display: 'block' }} />
                </span>
                Summary by Zana from {plural(runs.length, 'run')}
              </span>
              <p style={{ margin: 0, fontSize: 18, lineHeight: '28px', color: 'var(--ink2)' }}>
                Zana reads the runs, refills and buses for {periodLabel} and writes the summary for you. Nothing is written until you ask.
              </p>
              <button type="button" className="btn s b-line" style={{ alignSelf: 'flex-start', color: 'var(--z-ink)' }} onClick={() => ask(summaryQ)}>
                Write the summary
              </button>
            </section>

            <div className="stats">
              <div className="stat">
                <span className="k">Runs</span>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}><span className="big">{n0(runs.length)}</span></div>
                <span className="sm"><span style={{ color: 'var(--faint)' }}>finished or under way, cancelled left out</span></span>
              </div>
              <div className="stat">
                <span className="k">Kilometres</span>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}><span className="big">{kmKnown ? n0(km) : '—'}</span></div>
                <span className="sm"><span style={{ color: 'var(--faint)' }}>{kmKnown ? 'from the runs drivers recorded' : 'no run distances recorded yet'}</span></span>
              </div>
              <div className="stat">
                <span className="k">Fuel</span>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <span className="big"><span className="unit">Ksh</span>{n0(fuelKsh)}</span>
                </div>
                <span className="sm">
                  {fuelDelta != null && <span className={'delta ' + (fuelDelta > 0 ? 'dn-r' : 'up-g')}>{fuelDelta > 0 ? '+' : '−'}{Math.abs(fuelDelta)}%</span>}
                  <span style={{ color: 'var(--faint)' }}> {fuelDelta != null ? (period === '30d' ? 'vs the 30 days before' : 'vs the same days last month') : litres > 0 ? `${n0(litres)} litres logged` : 'from refills drivers logged'}</span>
                </span>
              </div>
              <div className="stat">
                <span className="k">Children carried</span>
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-end' }}><span className="big">{n0(carried)}</span></div>
                <span className="sm"><span style={{ color: 'var(--faint)' }}>pickups and drop-offs</span></span>
              </div>
            </div>

            <section className="panel" style={{ overflow: 'hidden' }}>
              <div className="ph">
                <h2 className="h2">By bus</h2>
                <span className="sm" style={{ color: 'var(--muted)' }}>Only columns with data are shown</span>
              </div>
              {rows.length === 0 ? (
                <Empty title="No bus has runs or refills in this period" body="Rows appear here as each bus finishes a run or logs a refill." />
              ) : (
                <div className="tblbox">
                  <table className="t" style={{ minWidth: 560 }}>
                    <thead>
                      <tr>
                        <th>Bus</th>
                        {cols.runs && <th className="r">Runs</th>}
                        {cols.km && <th className="r">Km</th>}
                        {cols.litres && <th className="r">Litres</th>}
                        {cols.ksh && <th className="r">Fuel, Ksh</th>}
                        {cols.kmpl && <th className="r">Km per litre</th>}
                        {cols.carried && <th className="r">Children carried</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => {
                        const dash = <span style={{ color: 'var(--faint)' }}>—</span>;
                        const link = go('ConsoleBuses', `?bus=${encodeURIComponent(r.id)}`);
                        return (
                          <tr key={r.id} onClick={() => navigate(link.href)}>
                            <td>
                              <div className="who">
                                <a href={link.href} onClick={(e) => { e.stopPropagation(); link.onClick(e); }} style={{ color: 'inherit', textDecoration: 'none' }}><b>{r.name}</b></a>
                                {plateBeside(r) && <span>{r.plate}</span>}
                              </div>
                            </td>
                            {cols.runs && <td className="r num">{r.runs || dash}</td>}
                            {cols.km && <td className="r num">{r.km != null ? n0(r.km) : dash}</td>}
                            {cols.litres && <td className="r num">{r.litres != null ? n0(r.litres) : dash}</td>}
                            {cols.ksh && <td className="r num">{r.ksh != null ? n0(r.ksh) : dash}</td>}
                            {cols.kmpl && <td className="r num">{r.kmpl != null ? r.kmpl.toFixed(1) : dash}</td>}
                            {cols.carried && <td className="r num">{r.carried ? n0(r.carried) : dash}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <p className="sm" style={{ margin: 0, color: 'var(--faint)', flex: 1, minWidth: 240 }}>
                The screen shows the last 30 days. An earlier month comes as a PDF:
              </p>
              <select className="tin" aria-label="Download an earlier month" value="" disabled={!!downloading}
                onChange={(e) => { const m = earlier.find((x) => x.key === e.target.value); if (m) download(m.key, m, 'pdf'); }}>
                <option value="">{downloading && earlier.some((m) => m.key === downloading) ? 'Making the PDF…' : 'Pick a month…'}</option>
                {earlier.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
              </select>
            </div>
          </>
        )}
      </div>
    </PageBody>
  );
};

export default Report;
